import { useEffect, useState } from "react";
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from "reactflow";
import {
  Paper,
  Text,
  Stack,
  Group,
  Badge,
  Loader,
  Center,
  Alert,
  ThemeIcon,
  Tooltip,
  Box,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconRobot,
  IconArrowRight,
  IconRefresh,
} from "@tabler/icons-react";
import { getAgentGraph } from "../api";
import { useLocation } from "wouter";

import "reactflow/dist/style.css";

// Custom node component for agent flows
function AgentFlowNode({ data }) {
  const [, setLocation] = useLocation();

  return (
    <>
      {/* Target handle at top - for incoming edges */}
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: "#868e96", width: 8, height: 8 }}
      />
      <Tooltip
        label={
          <Stack gap={2}>
            <Text size="xs" fw={500}>{data.label}</Text>
            {data.templateName && (
              <Text size="xs" c="dimmed">Entry: {data.templateName}</Text>
            )}
            {data.isExternal && (
              <Badge size="xs" color="gray">External</Badge>
            )}
          </Stack>
        }
        position="top"
        withArrow
      >
        <Paper
          withBorder
          p="sm"
          radius="md"
          shadow="sm"
          style={{
            minWidth: 140,
            backgroundColor: data.isCurrentFlow
              ? "#e7f5ff"
              : data.isExternal
              ? "#f8f9fa"
              : "#fff",
            borderColor: data.isCurrentFlow
              ? "#339af0"
              : data.isExternal
              ? "#adb5bd"
              : "#dee2e6",
            borderWidth: data.isCurrentFlow ? 2 : 1,
            cursor: "pointer",
          }}
          onClick={() => !data.isCurrentFlow && setLocation(`/flows/${data.flowId}`)}
        >
          <Group gap="xs" wrap="nowrap">
            <ThemeIcon
              size="sm"
              variant="light"
              color={data.isCurrentFlow ? "blue" : data.isExternal ? "gray" : "grape"}
            >
              <IconRobot size={14} />
            </ThemeIcon>
            <Stack gap={0}>
              <Text
                size="sm"
                fw={data.isCurrentFlow ? 600 : 500}
                truncate
                style={{ maxWidth: 120 }}
              >
                {data.label}
              </Text>
              {data.templateName && (
                <Text size="xs" c="dimmed" truncate style={{ maxWidth: 120 }}>
                  {data.templateName}
                </Text>
              )}
            </Stack>
          </Group>
        </Paper>
      </Tooltip>
      {/* Source handle at bottom - for outgoing edges */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: "#868e96", width: 8, height: 8 }}
      />
    </>
  );
}

const nodeTypes = {
  agentFlow: AgentFlowNode,
};

// Simple hierarchical layout (no external dependency)
function getLayoutedElements(nodes, edges) {
  const horizontalSpacing = 220;
  const verticalSpacing = 100;

  // Build adjacency list and find root node
  const childrenMap = {};
  const hasParent = new Set();

  edges.forEach((edge) => {
    if (!childrenMap[edge.source]) {
      childrenMap[edge.source] = [];
    }
    childrenMap[edge.source].push(edge.target);
    hasParent.add(edge.target);
  });

  // Find root nodes (nodes without parents)
  const rootNodes = nodes.filter((n) => !hasParent.has(n.id));
  if (rootNodes.length === 0 && nodes.length > 0) {
    rootNodes.push(nodes[0]);
  }

  // Calculate depth for each node using BFS
  const depths = {};
  const queue = [...rootNodes.map((n) => ({ id: n.id, depth: 0 }))];
  const visited = new Set();

  while (queue.length > 0) {
    const { id, depth } = queue.shift();
    if (visited.has(id)) continue;
    visited.add(id);
    depths[id] = depth;

    const children = childrenMap[id] || [];
    children.forEach((childId) => {
      if (!visited.has(childId)) {
        queue.push({ id: childId, depth: depth + 1 });
      }
    });
  }

  // Handle any unvisited nodes (disconnected or cycles)
  nodes.forEach((n) => {
    if (depths[n.id] === undefined) {
      depths[n.id] = Object.values(depths).length > 0 ? Math.max(...Object.values(depths)) + 1 : 0;
    }
  });

  // Group nodes by depth
  const depthGroups = {};
  nodes.forEach((n) => {
    const d = depths[n.id];
    if (!depthGroups[d]) depthGroups[d] = [];
    depthGroups[d].push(n);
  });

  // Position nodes
  const layoutedNodes = nodes.map((node) => {
    const depth = depths[node.id];
    const group = depthGroups[depth];
    const indexInGroup = group.indexOf(node);
    const groupWidth = group.length * horizontalSpacing;
    const startX = -groupWidth / 2 + horizontalSpacing / 2;

    return {
      ...node,
      position: {
        x: startX + indexInGroup * horizontalSpacing,
        y: depth * verticalSpacing,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
}

/**
 * Pre-execution agent graph visualization.
 * Shows potential agent relationships before running a flow.
 */
export default function AgentGraphPreview({ flowId, versionId }) {
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Fetch the agent graph data
  useEffect(() => {
    if (!flowId || !versionId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    getAgentGraph(flowId, versionId)
      .then((data) => {
        setGraphData(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch agent graph:", err);
        setError(err.message || "Failed to load agent graph");
        setLoading(false);
      });
  }, [flowId, versionId]);

  // Transform graph data to ReactFlow format
  useEffect(() => {
    if (!graphData) return;

    // Convert nodes
    const rfNodes = graphData.nodes.map((node) => ({
      id: node.id,
      type: "agentFlow",
      data: {
        label: node.label,
        flowId: node.flowId,
        flowName: node.flowName,
        templateName: node.templateName,
        isCurrentFlow: node.isCurrentFlow,
        isExternal: node.isExternal,
      },
      position: { x: 0, y: 0 },
    }));

    // Convert edges
    const rfEdges = graphData.edges.map((edge, index) => ({
      id: `edge-${index}`,
      source: edge.source,
      target: edge.target,
      label: edge.toolName,
      labelStyle: { fontSize: 10, fill: "#868e96" },
      labelBgStyle: { fill: "#fff", fillOpacity: 0.8 },
      labelBgPadding: [4, 2],
      labelBgBorderRadius: 4,
      type: "smoothstep",
      animated: edge.createsCycle,
      style: {
        stroke: edge.createsCycle ? "#fa5252" : "#868e96",
        strokeWidth: edge.createsCycle ? 2 : 1,
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: edge.createsCycle ? "#fa5252" : "#868e96",
      },
    }));

    // Apply layout
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      rfNodes,
      rfEdges
    );

    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  }, [graphData, setNodes, setEdges]);

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={16} />}>
        {error}
      </Alert>
    );
  }

  if (!graphData || graphData.nodes.length <= 1) {
    return (
      <Alert color="gray" icon={<IconRobot size={16} />}>
        This flow has no sub-agent relationships. Add agent-type tools to enable
        multi-agent orchestration.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {/* Summary stats */}
      <Group gap="lg">
        <Group gap={4}>
          <IconRobot size={14} color="gray" />
          <Text size="sm" c="dimmed">
            Flows:{" "}
            <Text span fw={600}>
              {graphData.nodes.length}
            </Text>
          </Text>
        </Group>
        <Group gap={4}>
          <IconArrowRight size={14} color="gray" />
          <Text size="sm" c="dimmed">
            Connections:{" "}
            <Text span fw={600}>
              {graphData.edges.length}
            </Text>
          </Text>
        </Group>
        <Group gap={4}>
          <IconRefresh size={14} color="gray" />
          <Text size="sm" c="dimmed">
            Max Depth:{" "}
            <Text span fw={600}>
              {graphData.maxPossibleDepth}
            </Text>
          </Text>
        </Group>
        {graphData.cycles.length > 0 && (
          <Badge color="red" leftSection={<IconAlertTriangle size={12} />}>
            {graphData.cycles.length} cycle(s) detected
          </Badge>
        )}
      </Group>

      {/* Cycle warnings */}
      {graphData.cycles.length > 0 && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
          <Text size="sm" fw={500} mb={4}>
            Potential infinite loops detected:
          </Text>
          {graphData.cycles.map((cycle, i) => (
            <Text key={i} size="xs" c="dimmed">
              {cycle.description}
            </Text>
          ))}
        </Alert>
      )}

      {/* Graph visualization */}
      <Paper withBorder radius="sm" style={{ height: 400 }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          attributionPosition="bottom-left"
          proOptions={{ hideAttribution: true }}
        >
          <Controls showInteractive={false} />
          <Background color="#e9ecef" gap={16} />
        </ReactFlow>
      </Paper>

      {/* Legend */}
      <Group gap="md">
        <Group gap={4}>
          <Box
            style={{
              width: 12,
              height: 12,
              backgroundColor: "#e7f5ff",
              border: "2px solid #339af0",
              borderRadius: 2,
            }}
          />
          <Text size="xs" c="dimmed">
            Current Flow
          </Text>
        </Group>
        <Group gap={4}>
          <Box
            style={{
              width: 12,
              height: 12,
              backgroundColor: "#fff",
              border: "1px solid #dee2e6",
              borderRadius: 2,
            }}
          />
          <Text size="xs" c="dimmed">
            Sub-Agent
          </Text>
        </Group>
        <Group gap={4}>
          <Box
            style={{
              width: 12,
              height: 12,
              backgroundColor: "#f8f9fa",
              border: "1px solid #adb5bd",
              borderRadius: 2,
            }}
          />
          <Text size="xs" c="dimmed">
            External/Inaccessible
          </Text>
        </Group>
        <Group gap={4}>
          <Box
            style={{
              width: 20,
              height: 2,
              backgroundColor: "#fa5252",
            }}
          />
          <Text size="xs" c="dimmed">
            Cycle Edge
          </Text>
        </Group>
      </Group>
    </Stack>
  );
}
